/**
 * pushApi — HTTP client for /api/v1/notifications.
 *
 * Small wrapper over axios so the useWebPush hook (and the Settings UI)
 * don't have to remember URL shapes. The endpoints live under
 * /notifications; response bodies match the server route in routes/notifications.ts.
 */
import { api } from './api';

export interface NotificationRule {
  id: string;
  mode: 'ALLOW' | 'BLOCK';
  geofenceId: string | null;
  alertTypes: string[];
  minSeverity: 'INFO' | 'WARNING' | 'CRITICAL' | null;
  label: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationRuleInput {
  mode: 'ALLOW' | 'BLOCK';
  geofenceId?: string | null;
  alertTypes?: string[];
  minSeverity?: 'INFO' | 'WARNING' | 'CRITICAL' | null;
  label?: string;
}

export const pushApi = {
  /** Fetch the server's VAPID public key. Returns null when push is
   *  disabled (no keys configured), so the caller can hide the UI cleanly. */
  getVapidPublicKey: async (): Promise<string | null> => {
    try {
      const res = await api.get('/notifications/vapid-public-key');
      return res.data?.data?.publicKey ?? null;
    } catch {
      // 503 when server hasn't been given VAPID keys. Treat as "push off".
      return null;
    }
  },

  /** Upsert the current browser's subscription on the server. */
  subscribe: async (subscription: PushSubscription, userAgent?: string): Promise<void> => {
    const json = subscription.toJSON();
    await api.post('/notifications/subscriptions', {
      endpoint: json.endpoint,
      keys: json.keys,
      userAgent,
    });
  },

  /** Tell the server to forget this browser's subscription. */
  unsubscribe: async (endpoint: string): Promise<void> => {
    await api.delete('/notifications/subscriptions', { params: { endpoint } });
  },

  listRules: async (): Promise<NotificationRule[]> => {
    const res = await api.get('/notifications/rules');
    return res.data?.data ?? [];
  },

  createRule: async (rule: NotificationRuleInput): Promise<string> => {
    const res = await api.post('/notifications/rules', rule);
    return res.data?.data?.id;
  },

  updateRule: async (id: string, rule: Partial<NotificationRuleInput>): Promise<void> => {
    await api.patch(`/notifications/rules/${id}`, rule);
  },

  deleteRule: async (id: string): Promise<void> => {
    await api.delete(`/notifications/rules/${id}`);
  },
};
